import { Navigate, useParams } from '@tanstack/react-router';
import { useAuth } from 'app/javascript/contexts/AuthContext';
import { ROUTES } from 'app/javascript/constants/routes';
import { useAdminCompanyHealth } from 'app/javascript/queries/useAdmin';
import { Skeleton } from 'app/javascript/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'app/javascript/components/ui/tabs';
import CompanyHealthHeader from 'app/javascript/components/admin/company_health/CompanyHealthHeader';
import HealthAlerts from 'app/javascript/components/admin/company_health/HealthAlerts';
import StaffPanel from 'app/javascript/components/admin/company_health/StaffPanel';
import EngagementPanel from 'app/javascript/components/admin/company_health/EngagementPanel';
import PlanPanel from 'app/javascript/components/admin/company_health/PlanPanel';
import MembersPanel from 'app/javascript/components/admin/company_health/MembersPanel';
import ActivityPanel from 'app/javascript/components/admin/company_health/ActivityPanel';
import BillingPanel from 'app/javascript/components/admin/company_health/BillingPanel';
import CompanyMembersTable from 'app/javascript/components/admin/company_health/CompanyMembersTable';
import TrainingPhotosGrid from 'app/javascript/components/admin/company_health/TrainingPhotosGrid';

function HealthSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-14 w-72 rounded-lg" />
      <Skeleton className="h-12 w-full rounded-lg" />
      <Skeleton className="h-40 w-full rounded-lg" />
      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-64 rounded-lg lg:col-span-2" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    </div>
  );
}

// Owner-only per-company debug page. Same gating as AdminPage: the check here
// is UX only, the API returns 403 for anyone who is not the app owner.
//
// Reading order is diagnosis first: alerts say what is wrong, the staff panel
// says whether anyone can act on it, and only then come the numbers.
export default function AdminCompanyPage() {
  const { currentUser } = useAuth();
  const { slug } = useParams({ from: ROUTES.ADMIN_COMPANY });
  const { data, isLoading, isError } = useAdminCompanyHealth(slug);

  if (!currentUser?.appAdmin) {
    return <Navigate to={ROUTES.HOME} />;
  }

  if (isLoading) return <HealthSkeleton />;

  if (isError || !data) {
    return <p className="text-sm text-destructive">Não foi possível carregar a academia. Verifique o link e tente novamente.</p>;
  }

  return (
    <div className="space-y-6">
      <CompanyHealthHeader company={data} />
      <HealthAlerts alerts={data.alerts} />
      <StaffPanel staff={data.staff} />
      <EngagementPanel company={data} />

      <div className="grid gap-4 lg:grid-cols-3">
        <PlanPanel plan={data.plan} memberCount={data.members.total} slug={slug} className="lg:col-span-2" />
        <MembersPanel members={data.members} />
        <ActivityPanel activity={data.activity} className="lg:col-span-2" />
        <BillingPanel billing={data.billing} />
      </div>

      <Tabs defaultValue="members" className="space-y-4">
        <TabsList className="h-auto flex-wrap bg-secondary/60">
          <TabsTrigger value="members">Membros</TabsTrigger>
          <TabsTrigger value="photos">Fotos dos treinos</TabsTrigger>
        </TabsList>
        <TabsContent value="members">
          <CompanyMembersTable slug={slug} />
        </TabsContent>
        <TabsContent value="photos">
          <TrainingPhotosGrid slug={slug} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
